import React, { useState, useEffect } from 'react';
import { codeToTokens } from 'shiki';
import type { Code as Interface } from '../../design/components';

export const Code: React.FC<Interface> = ({
  name, description, state, hidden, inline, readonly, disabled, required, error, multiline = 14
}) => {
  const [value, setValue] = state || ['', () => {}];
  const [tokens, setTokens] = useState<{ content: string; color?: string }[][]>([]);

  useEffect(() => {
    const code = value || '';
    if (!code) {
      // setState-in-effect is intentional: tokens are derived from value;
      // an empty value synchronously clears them. No cascade risk — the
      // effect depends on `value` alone.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTokens([]);
      return;
    }
    codeToTokens(code, { lang: 'javascript', theme: 'github-light' })
      .then(result => {
        setTokens(result.tokens.map(line =>
          line.map(token => ({ content: token.content, color: token.color }))
        ));
      })
      .catch(() => setTokens([]));
  }, [value]);

  // Inline mode: render just the code as monospace text with highlighting
  if (inline) {
    return (
      <code
        style={{
          display: hidden ? 'none' : 'inline',
          fontFamily: 'monospace',
          fontSize: '0.875em',
          whiteSpace: 'pre-wrap',
        }}
      >
        {tokens.length > 0 ? (
          tokens[0]?.map((token, i) => (
            <span key={i} style={{ color: token.color }}>
              {token.content}
            </span>
          ))
        ) : (value || '')}
      </code>
    );
  }

  const baseStyles: React.CSSProperties = {
    fontFamily: 'monospace',
    fontSize: '14px',
    lineHeight: '1.5',
    tabSize: 2,
    margin: 0,
    padding: '12px',
    border: 0,
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  };

  const actualLines = (value || '').split('\n').length;
  const lineCount = Math.min(Math.max(actualLines, 1), multiline);
  const atMax = actualLines > multiline;
  const lineHeightPx = 21; // 14px font * 1.5 line-height
  const maxHeight = atMax ? `${multiline * lineHeightPx + 24}px` : undefined; // 24px for padding

  return (
    <div style={{ marginBottom: '1rem', display: hidden ? 'none' : undefined }}>
      <label htmlFor={name} style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
        {name}
        {required && <span style={{ color: 'red' }}> *</span>}
      </label>
      <div
        style={{
          display: 'grid',
          borderRadius: '4px',
          border: error ? '1px solid red' : '1px solid #ccc',
          backgroundColor: '#f8f8f8',
          maxHeight,
          overflow: atMax ? 'auto' : undefined,
        }}
      >
        {/* Highlighted code layer (behind) */}
        <pre
          aria-hidden="true"
          style={{
            ...baseStyles,
            gridArea: '1 / 1 / 2 / 2',
            overflow: 'hidden',
            pointerEvents: 'none',
            backgroundColor: 'transparent',
          }}
        >
          {tokens.length > 0 ? (
            tokens.map((line, lineIdx) => (
              <React.Fragment key={lineIdx}>
                {line.map((token, tokenIdx) => (
                  <span key={tokenIdx} style={{ color: token.color }}>
                    {token.content}
                  </span>
                ))}
                {lineIdx < tokens.length - 1 && '\n'}
              </React.Fragment>
            ))
          ) : (value || '') + '\n'}
        </pre>
        {/* Transparent textarea (in front for input) */}
        <textarea
          id={name}
          name={name}
          value={value || ''}
          disabled={disabled}
          readOnly={readonly}
          required={required}
          rows={lineCount}
          onChange={(e) => setValue(e.target.value)}
          spellCheck={false}
          style={{
            ...baseStyles,
            gridArea: '1 / 1 / 2 / 2',
            width: '100%',
            boxSizing: 'border-box',
            resize: 'none',
            backgroundColor: 'transparent',
            color: 'transparent',
            caretColor: 'black',
            outline: 'none',
          }}
        />
      </div>
      {(error || description) && (
        <small style={{ color: error ? 'red' : '#666', display: 'block', marginTop: '4px' }}>
          {error || description}
        </small>
      )}
    </div>
  );
};
